'use client';

import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text, IconButton, Badge, Tooltip } from '@terra/ui-design-system';
import {
  ArrowDataTransferHorizontalIcon,
  ClipboardPasteIcon,
  Delete02Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useCallback } from 'react';
import { useComparerStore } from '../../store/comparer-store';

type ComparerItemListProps = {
  onPaste?: () => void;
};

export function ComparerItemList({ onPaste }: ComparerItemListProps) {
  const {
    items,
    selectedLeftId,
    selectedRightId,
    setSelectedLeft,
    setSelectedRight,
    removeItem,
    clearItems,
    swapSelection,
  } = useComparerStore();
  const handleSelectItem = useCallback(
    (id: string, side: 'left' | 'right') => {
      if (side === 'left') {
        if (selectedRightId === id) {
          setSelectedRight(selectedLeftId);
        }

        setSelectedLeft(id);
      } else {
        if (selectedLeftId === id) {
          setSelectedLeft(selectedRightId);
        }

        setSelectedRight(id);
      }
    },
    [selectedLeftId, selectedRightId, setSelectedLeft, setSelectedRight],
  );

  return (
    <Box
      w="200px"
      borderRightWidth="1px"
      borderColor="border.default"
      bg="bg.subtle"
      display="flex"
      flexDirection="column"
      flexShrink={0}
    >
      {/* Header */}
      <HStack
        p="1"
        borderBottomWidth="1px"
        borderColor="border.default"
        justifyContent="space-between"
        alignItems="center"
      >
        <HStack gap="1" alignItems="center">
          <Text fontSize="xs" color="fg.muted">
            Items
          </Text>
          <Badge variant="emphasis" size="sm">
            {items.length}
          </Badge>
        </HStack>
        <HStack gap="0">
          {items.length >= 2 && (
            <Tooltip title="Swap">
              <IconButton
                size="xs"
                variant="ghost"
                aria-label="Swap selection"
                onClick={swapSelection}
              >
                <ArrowDataTransferHorizontalIcon size={12} />
              </IconButton>
            </Tooltip>
          )}
          {onPaste && (
            <Tooltip title="Paste">
              <IconButton
                size="xs"
                variant="ghost"
                aria-label="Paste"
                onClick={onPaste}
              >
                <ClipboardPasteIcon size={12} />
              </IconButton>
            </Tooltip>
          )}
          {items.length > 0 && (
            <Tooltip title="Clear all">
              <IconButton
                size="xs"
                variant="ghost"
                aria-label="Clear all"
                onClick={clearItems}
              >
                <Delete02Icon size={12} />
              </IconButton>
            </Tooltip>
          )}
        </HStack>
      </HStack>

      {/* Items List */}
      <Box flex="1" overflow="auto">
        <VStack gap="0" alignItems="stretch">
          {items.length === 0 ? (
            <Box py="6" px="2" textAlign="center">
              <Text fontSize="xs" color="fg.muted">
                No items yet
              </Text>
              <Text fontSize="xs" color="fg.muted" mt="1">
                Right-click → Send to Comparer
              </Text>
            </Box>
          ) : (
            items.map((item) => {
              const isLeft = item.id === selectedLeftId;
              const isRight = item.id === selectedRightId;

              return (
                <HStack
                  key={item.id}
                  px="2"
                  py="1.5"
                  gap="2"
                  alignItems="center"
                  borderBottomWidth="1px"
                  borderColor="border.subtle"
                  bg={isLeft || isRight ? 'bg.muted' : 'transparent'}
                  _hover={{ bg: 'bg.muted' }}
                  role="group"
                >
                  {/* L/R indicators - clickable */}
                  <HStack gap="0.5" flexShrink={0}>
                    <Box
                      as="button"
                      w="16px"
                      h="16px"
                      borderRadius="sm"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="9px"
                      fontWeight="bold"
                      cursor="pointer"
                      bg={isLeft ? 'colorPalette.default' : 'transparent'}
                      color={isLeft ? 'colorPalette.fg' : 'fg.muted'}
                      borderWidth="1px"
                      borderColor={
                        isLeft ? 'colorPalette.default' : 'border.default'
                      }
                      onClick={() => handleSelectItem(item.id, 'left')}
                      _hover={{ borderColor: 'border.outline' }}
                    >
                      L
                    </Box>
                    <Box
                      as="button"
                      w="16px"
                      h="16px"
                      borderRadius="sm"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="9px"
                      fontWeight="bold"
                      cursor="pointer"
                      bg={isRight ? 'colorPalette.default' : 'transparent'}
                      color={isRight ? 'colorPalette.fg' : 'fg.muted'}
                      borderWidth="1px"
                      borderColor={
                        isRight ? 'colorPalette.default' : 'border.default'
                      }
                      onClick={() => handleSelectItem(item.id, 'right')}
                      _hover={{ borderColor: 'border.outline' }}
                    >
                      R
                    </Box>
                  </HStack>

                  {/* Label and type */}
                  <Box flex="1" minW={0}>
                    <Tooltip title={item.label}>
                      <Text
                        fontSize="xs"
                        color="fg.default"
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.label}
                      </Text>
                    </Tooltip>
                    <Text fontSize="2xs" color="fg.muted">
                      {item.type}
                    </Text>
                  </Box>

                  {/* Remove - visible on hover */}
                  <Box
                    opacity={0}
                    _groupHover={{ opacity: 1 }}
                    transition="opacity 0.1s"
                    flexShrink={0}
                  >
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Remove"
                      onClick={() => removeItem(item.id)}
                    >
                      <Cancel01Icon size={12} />
                    </IconButton>
                  </Box>
                </HStack>
              );
            })
          )}
        </VStack>
      </Box>
    </Box>
  );
}
